import React, { useState } from "react";
import {
  Accordion,
  AccordionGroup,
  Card,
  EmptyState,
  Flex,
  Text,
  LoadingIndicator,
  Tag,
  Button,
} from "@jsq/ds/core";
import { ScreeningMatchResult } from "~/graphql/supergraphEnums";
import { type ScreeningMatchType } from "~/graphql/supergraphTypes";
import { useEnableAiScreening } from "~/pages/Compliance/flags";
import { formatShortFormDate } from "~/utils/datetime";
import { InternalPepCategory, ScreeningMatchUpdatedResults, SearchInput } from "../../types";
import EvaluationSummary from "./EvaluationSummary";
import ProfileDetailsModal from "./ProfileDetailsModal";
import { ScreeningMatchDetails } from "./ScreeningMatchDetails";
import { matchDetailsId_style } from "./ScreeningMatchDetails/styles_vanilla.css";
import {
  LexisNexisMatchesCard_Style,
  LexisNexisMatchesColumnContainer_Style,
  LexisNexisMatchesContainer_Style,
  LexisNexisMatchesAccordionCell_Style,
  AiResultContainer_Style,
  AiAnalysisSection_Style,
  LexisNexisMatchesAccordionGroup_Style,
  LexisNexisMatchesHeader_Style,
  LexisNexisMatchesResultUpdatedTime,
  lexisNexisMatchesViewProfileButton,
  LexisNexisMatchesResultDeleted,
  accordionMatches,
} from "./styles_vanilla.css";
import {
  extractName,
  findMatchById,
  getProfileScreeningResult,
  mapPepCategoryToNumber,
  sortTimelineMatchesByResult,
  getRiskLevel,
} from "./utils";

interface Props {
  screeningMatches: ScreeningMatchType[];
  matchResults: ScreeningMatchUpdatedResults[];
  setMatchResults: (updatedResults: ScreeningMatchUpdatedResults[]) => void;
  readonly: boolean;
  aiScoringResults: Map<
    number,
    { ai_score: number; explanation: string; recommended_action: string }
  >;
  isAiLoading?: boolean;
  aiProcessed?: boolean;
  isPolling?: boolean;
  riskAssessmentFailed?: boolean;
}

const LexisNexisMatches: React.FC<Props> = ({
  screeningMatches,
  matchResults,
  setMatchResults,
  readonly,
  aiScoringResults,
  isAiLoading = false,
  aiProcessed = false,
  isPolling = false,
  riskAssessmentFailed = false,
}) => {
  const enableAIScreening = useEnableAiScreening();

  const lexisNexisMatchesFound = screeningMatches.length > 0;

  const [lexisNexisProfileModal, setLexisNexisProfileModal] = useState(false);
  const currentCaseId = screeningMatches?.[0]?.caseId ?? -1;
  const latestModifiedDate = screeningMatches
    .filter((match: ScreeningMatchType) => match.modifiedAt)
    .sort(
      (a, b) => new Date(b.modifiedAt ?? "").getTime() - new Date(a.modifiedAt ?? "").getTime(),
    )[0]?.modifiedAt;

  const searchInput = (screeningMatches.find((match) => match.searchInput)?.searchInput ??
    null) as SearchInput | null;

  const isScreeningMatchResultType = (
    input: string | number,
  ): input is ScreeningMatchResult => {
    return (
      typeof input === "string" &&
      ["FalsePositive", "GatheringInformation", "TrueMatch", "Unknown"].includes(input)
    );
  };

  const isInternalPepCategory = (input: string | number): input is InternalPepCategory => {
    return (
      typeof input === "string" &&
      ["NA", "pepCategory1", "pepCategory2", "pepCategory3"].includes(input)
    );
  };

  const handleOnInternalUseOptionSelect = (
    optionSelected: string | number,
    matchId: number,
  ) => {
    const currentSelectedMatchPepResult =
      findMatchById(matchId, matchResults)?.pepCategory || undefined;
    let computedPepCategory = currentSelectedMatchPepResult;
    if (
      isScreeningMatchResultType(optionSelected) &&
      optionSelected !== ScreeningMatchResult.TrueMatch
    ) {
      computedPepCategory = undefined;
    }
    setMatchResults([
      ...matchResults.filter(
        (matchResult: ScreeningMatchUpdatedResults) => matchResult.id !== matchId,
      ),
      {
        id: matchId,
        result: isScreeningMatchResultType(optionSelected)
          ? optionSelected
          : ScreeningMatchResult.Unknown,
        pepCategory: computedPepCategory,
      },
    ]);
  };

  const handleOnPEPOptionSelect = (optionSelected: string | number, matchId: number) => {
    setMatchResults([
      ...matchResults.filter(
        (matchResult: ScreeningMatchUpdatedResults) => matchResult.id !== matchId,
      ),
      {
        id: matchId,
        // PEP dropdown is UI-gated to True Match only, so pin the result here to
        // avoid saving Unknown + pepCategory when matchResults is stale/empty.
        result: ScreeningMatchResult.TrueMatch,
        pepCategory:
          mapPepCategoryToNumber[isInternalPepCategory(optionSelected) ? optionSelected : "NA"],
      },
    ]);
  };

  let aiStatusContent = null;
  if (isAiLoading || isPolling) {
    aiStatusContent = (
      <>
        <LoadingIndicator size="small" />
        <Text variant="secondary-sm">AI Processing</Text>
      </>
    );
  } else if (riskAssessmentFailed) {
    aiStatusContent = <Text variant="secondary-sm">AI Processing failed</Text>;
  } else if (aiProcessed) {
    aiStatusContent = <Text variant="secondary-sm">AI Processed matches</Text>;
  }
  const isReadOnly = (lnMatch: ScreeningMatchType) => {
    return readonly || lnMatch.immutable;
  };

  const showAccordionSubtitle = (lnMatch: ScreeningMatchType) => {
    return Boolean(lnMatch.deletedAt);
  };

  const getAccordionSubtitle = (lnMatch: ScreeningMatchType) => {
    if (showAccordionSubtitle(lnMatch)) {
      if (lnMatch.deletedAt) {
        return `Deleted`;
      }
      return `Evaluated on ${formatShortFormDate(String(lnMatch.modifiedAt))}`;
    }
    return undefined;
  };

  const getSubtitleClass = (lnMatch: ScreeningMatchType) => {
    if (!showAccordionSubtitle(lnMatch)) return undefined;
    return lnMatch.deletedAt
      ? LexisNexisMatchesResultDeleted
      : LexisNexisMatchesResultUpdatedTime;
  };

  return (
    <Card className={LexisNexisMatchesCard_Style}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        className={LexisNexisMatchesHeader_Style}
      >
        <Flex flexDirection="row" gap={16} alignItems="center">
          <Text variant="h3">LexisNexis matches</Text>
          {lexisNexisMatchesFound &&
            (currentCaseId === -1 ? (
              <Tag variant="warning" label="Review Needed" />
            ) : (
              <Tag
                variant="neutral"
                label={`Evaluated on ${formatShortFormDate(latestModifiedDate)}`}
              />
            ))}
        </Flex>
        {lexisNexisMatchesFound && (
          <Flex alignItems="center" gap={8}>
            {aiStatusContent}
          </Flex>
        )}
        {currentCaseId !== -1 && (
          <>
            <Button
              className={lexisNexisMatchesViewProfileButton}
              icon="arrow-right"
              iconPosition="right"
              displayAsLink
              onClick={() => {
                setLexisNexisProfileModal(!lexisNexisProfileModal);
              }}
            >
              View profile sent to LexisNexis
            </Button>
            {lexisNexisProfileModal && (
              <ProfileDetailsModal
                show={lexisNexisProfileModal}
                onClose={() => setLexisNexisProfileModal(false)}
                searchInput={searchInput}
                date={latestModifiedDate}
              />
            )}
          </>
        )}
      </Flex>
      {lexisNexisMatchesFound ? (
        <AccordionGroup className={LexisNexisMatchesAccordionGroup_Style}>
          {sortTimelineMatchesByResult(screeningMatches).map((lnMatch: ScreeningMatchType) => {
            const aiResult = aiScoringResults.get(lnMatch.id);
            const matchName = extractName(lnMatch);

            // Create title with Badge for AI score
            const titleContent = (
              <Flex alignItems="center" gap={8}>
                <Text>{matchName}</Text>
                <Text asSpan className={matchDetailsId_style}>
                  ({lnMatch.id})
                </Text>
                {aiResult && (
                  <Tag variant={getRiskLevel(aiResult.ai_score).variant}>
                    Score: {aiResult.ai_score}
                  </Tag>
                )}
              </Flex>
            );

            return (
              <Accordion
                className={accordionMatches}
                title={titleContent}
                titleClass={LexisNexisMatchesAccordionCell_Style}
                statusText={getProfileScreeningResult(lnMatch.result)?.text}
                statusVariant={getProfileScreeningResult(lnMatch.result)?.variant ?? "warning"}
                subtitle={getAccordionSubtitle(lnMatch)}
                subtitleClass={getSubtitleClass(lnMatch)}
                data-testid="lexis-nexis-match-accordion"
                key={lnMatch.id}
              >
                <Flex className={LexisNexisMatchesContainer_Style}>
                  <Flex className={LexisNexisMatchesColumnContainer_Style}>
                    <ScreeningMatchDetails lnMatch={lnMatch} />
                  </Flex>
                  <Flex className={LexisNexisMatchesColumnContainer_Style}>
                    <EvaluationSummary
                      lnMatch={lnMatch}
                      isReadOnly={isReadOnly(lnMatch)}
                      onResultOptionSelect={handleOnInternalUseOptionSelect}
                      onPEPOptionSelect={handleOnPEPOptionSelect}
                      matchResults={matchResults}
                    />
                    {enableAIScreening && (
                      <Flex className={AiResultContainer_Style} flexDirection="column">
                        <Text variant="strong-lg">AI Analysis</Text>
                        <Flex className={AiAnalysisSection_Style}>
                          <Text variant="strong-md">AI Score</Text>
                          <Text variant="regular-md">{aiResult?.ai_score}</Text>
                        </Flex>
                        <Flex className={AiAnalysisSection_Style}>
                          <Text variant="strong-md">Score Explanation</Text>
                          <Text variant="regular-md">{aiResult?.explanation}</Text>
                        </Flex>
                        <Flex className={AiAnalysisSection_Style}>
                          <Text variant="strong-md">Recommended Action</Text>
                          <Text variant="regular-md">{aiResult?.recommended_action}</Text>
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Accordion>
            );
          })}
        </AccordionGroup>
      ) : (
        <EmptyState
          illustration="document-success"
          title="No matches found"
          description="No individuals or entities were found in LexisNexis"
        />
      )}
    </Card>
  );
};

export default LexisNexisMatches;
